/**
 * What every section of the template settings page receives, and the small
 * pieces they share. One section per switch group of
 * SPEC-custom-agent-template.md §2.
 */

import type { ReactNode } from 'react';
import { Button } from '../../ui/Button';
import { Panel, Switch } from '../../ds';
import type { AgentSettings } from '../../../lib/agentSettingsService';
import type { AgentDefinition } from '../../../lib/ai/agentDefinitions';
import type { AgentSettingsUpdate } from '../../../stores/prioritization/agentSettingsStore';

export interface SectionProps {
  def: AgentDefinition;
  config: AgentSettings | null;
  workspaceId: string;
  userId: string | null;
  isSaving: boolean;
  /** Only a workspace administrator changes an agent's settings (the database checks the same). */
  canEdit: boolean;
  save: (updates: AgentSettingsUpdate, okTitle?: string) => Promise<void>;
}

/** The template's on/off switch: the design-system Switch, with the label spoken, not shown. */
export function Toggle({ on, onChange, disabled, label }: { on: boolean; onChange: () => void; disabled?: boolean; label: string }) {
  return <Switch checked={on} onChange={() => onChange()} disabled={disabled} aria-label={label} />;
}

/** One switch group: the design-system Panel with a plain lead line under the title. */
export function SwitchGroup({ title, lead, children, action }: { title: string; lead?: ReactNode; children: ReactNode; action?: ReactNode }) {
  return (
    <Panel title={title} actions={action}>
      <div className="space-y-3">
        {lead && <p className="text-xs text-[var(--text-ter)]">{lead}</p>}
        {children}
      </div>
    </Panel>
  );
}

/** A row: what the switch is, what it does, and the switch. */
export function SwitchRow({ label, help, on, disabled, onChange }: { label: string; help?: string; on: boolean; disabled?: boolean; onChange: () => void }) {
  return (
    <div className="flex items-start justify-between gap-3 border-t border-[var(--rule)] pt-3">
      <div>
        <p className="text-sm font-medium text-[var(--text-pri)]">{label}</p>
        {help && <p className="text-[11px] text-[var(--text-ter)]">{help}</p>}
      </div>
      <Toggle on={on} disabled={disabled} label={label} onChange={onChange} />
    </div>
  );
}

/** Pick several from a list: one button per option, pressed when chosen. */
export function ChipPicker({ options, chosen, disabled, onToggle }: {
  options: { key: string; label: string }[];
  chosen: string[];
  disabled?: boolean;
  onToggle: (key: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = chosen.includes(o.key);
        return (
          <Button key={o.key} variant={on ? 'primary' : 'secondary'} size="sm" disabled={disabled} aria-pressed={on} onClick={() => onToggle(o.key)}>
            {o.label}
          </Button>
        );
      })}
    </div>
  );
}

